// The @ site picker (16 Oct 2026): same anchored glass panel as the slash menu. Each row
// shows the site's favicon, business name and domain; the highlighted row shows a preview
// of the website (its share image) so you can see it is the right one before picking it.
// The Composer owns open/query/selection; this component only draws.
import { useEffect, useRef, useState } from "react";
import { loadSitePreview, onSiteTagsChange, sitePreview, tagDisplayName, type SiteTag } from "../lib/siteTags";
import { authFetch } from "../auth-context";

interface Props {
  open: boolean;
  query: string;
  tags: SiteTag[];
  loading: boolean;
  selectedIndex: number;
  onSelect: (tag: SiteTag) => void;
  onHover: (index: number) => void;
}

export function SiteFavicon({ tag, size = 28 }: { tag: SiteTag; size?: number }) {
  const [, force] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => { loadSitePreview(authFetch, tag.id); return onSiteTagsChange(() => force((n) => n + 1)); }, [tag.id]);
  const icon = sitePreview(tag.id)?.favicon;
  const letter = tagDisplayName(tag).replace(/[^A-Za-z0-9]/g, "").slice(0, 1).toUpperCase() || "@";
  return (
    <span className="tagMenu__icon" style={{ width: size, height: size }} aria-hidden="true">
      {icon && !failed ? <img src={icon} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} /> : <span>{letter}</span>}
    </span>
  );
}

export default function TagMenu({ open, query, tags, loading, selectedIndex, onSelect, onHover }: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const [, force] = useState(0);
  const active = tags[Math.max(0, Math.min(tags.length - 1, selectedIndex))];
  useEffect(() => onSiteTagsChange(() => force((n) => n + 1)), []);
  useEffect(() => { if (open && active) loadSitePreview(authFetch, active.id); }, [open, active?.id]);
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${selectedIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, selectedIndex]);
  if (!open) return null;
  const preview = active ? sitePreview(active.id) : null;
  return (
    <div className="slashMenu tagMenu" role="listbox" aria-label="Tag a website">
      <div className="slashMenu__header">
        <span className="slashMenu__title">Tag a website</span>
        <span className="slashMenu__hint">↑↓ choose · ↵ tag · esc close</span>
      </div>
      {active && (
        <a className="tagMenu__preview" href={active.url} target="_blank" rel="noopener noreferrer"
          onMouseDown={(e) => e.preventDefault()} title="Open the live site in a new tab">
          {preview?.image
            ? <img src={preview.image} alt="" loading="lazy" referrerPolicy="no-referrer" />
            : <span className="tagMenu__previewBlank"><SiteFavicon tag={active} size={36} /></span>}
          <span className="tagMenu__previewText">
            <b>{tagDisplayName(active)}</b>
            <span>{active.host}</span>
          </span>
        </a>
      )}
      <div className="slashMenu__list" ref={listRef}>
        {tags.length === 0 ? (
          <div className="slashMenu__empty">{loading ? "Loading websites…" : <>No website matches <code>@{query}</code></>}</div>
        ) : tags.map((t, i) => (
          <button key={t.id} type="button" role="option" aria-selected={i === selectedIndex} data-idx={i}
            className={`tagMenu__item ${i === selectedIndex ? "is-active" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(t)} onMouseEnter={() => onHover(i)}>
            <SiteFavicon tag={t} />
            <span className="tagMenu__text">
              <span className="tagMenu__name">{tagDisplayName(t)}</span>
              <span className="tagMenu__host">@{t.handle} · {t.host}{t.kind === "sample" ? " · sample" : ""}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
